import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ArrowLeft, Calendar, ChevronDown, ChevronUp, BarChart2, MessageSquare, MapPin } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
type PeriodTab = 'Week' | 'Month' | 'Year';
const DATE_OPTIONS: Record<PeriodTab, {
  label?: string;
  range: string;
}[]> = {
  Week: [{
    label: 'This Week',
    range: 'Jan 26 - Feb 1, 2026'
  }, {
    range: 'Jan 19 - Jan 25, 2026'
  }, {
    range: 'Jan 11 - Jan 19, 2026'
  }, {
    range: 'Jan 5 - Jan 11, 2026'
  }, {
    range: 'Dec 30 - Jan 4, 2026'
  }],
  Month: [{
    label: 'This Month',
    range: 'Jan 1 - Jan 31, 2026'
  }, {
    range: 'Dec 1 - Dec 31, 2025'
  }, {
    range: 'Nov 1 - Nov 30, 2025'
  }, {
    range: 'Oct 1 - Oct 31, 2025'
  }, {
    range: 'Sep 1 - Sep 30, 2025'
  }],
  Year: [{
    label: 'This Year',
    range: 'Jan 1 - Dec 31, 2026'
  }, {
    range: 'Jan 1 - Dec 31, 2025'
  }, {
    range: 'Jan 1 - Dec 31, 2024'
  }]
};
const TABS: PeriodTab[] = ['Year', 'Month', 'Week'];
export function ChatArea() {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PeriodTab>('Week');
  const [selectedIndex, setSelectedIndex] = useState<Record<PeriodTab, number>>({
    Week: 0,
    Month: 0,
    Year: 0
  });
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const currentOptions = DATE_OPTIONS[activeTab];
  const currentSelected = currentOptions[selectedIndex[activeTab]];
  const displayLabel = currentSelected.label ? `${currentSelected.label}  •  ${currentSelected.range}` : currentSelected.range;
  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) && triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsDatePickerOpen(false);
        setFocusedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Focus management when dropdown opens
  useEffect(() => {
    if (isDatePickerOpen) {
      const idx = selectedIndex[activeTab];
      setFocusedIndex(idx);
      setTimeout(() => {
        optionRefs.current[idx]?.focus();
      }, 50);
    }
  }, [isDatePickerOpen, activeTab, selectedIndex]);
  const toggleDropdown = useCallback(() => {
    setIsDatePickerOpen(prev => !prev);
  }, []);
  const selectOption = useCallback((index: number) => {
    setSelectedIndex(prev => ({
      ...prev,
      [activeTab]: index
    }));
    setIsDatePickerOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  }, [activeTab]);
  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsDatePickerOpen(false);
      setFocusedIndex(-1);
    } else if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !isDatePickerOpen) {
      e.preventDefault();
      setIsDatePickerOpen(true);
    }
  }, [isDatePickerOpen]);
  const handleOptionKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    const options = DATE_OPTIONS[activeTab];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        {
          const next = index < options.length - 1 ? index + 1 : 0;
          setFocusedIndex(next);
          optionRefs.current[next]?.focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        {
          const prev = index > 0 ? index - 1 : options.length - 1;
          setFocusedIndex(prev);
          optionRefs.current[prev]?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectOption(index);
        break;
      case 'Escape':
        e.preventDefault();
        setIsDatePickerOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
        break;
      case 'Tab':
        setIsDatePickerOpen(false);
        setFocusedIndex(-1);
        break;
    }
  }, [activeTab, selectOption]);
  return <main className="flex-1 flex flex-col h-full bg-white min-w-0" data-id="element-4">
      {/* Header */}
      <header className="h-20 border-b border-gray-100 flex items-center justify-between px-8 flex-shrink-0" data-id="element-5">
        <div className="flex items-center" data-id="element-6">
          <button className="mr-6 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7">
            <ArrowLeft className="h-5 w-5" data-id="element-8" />
          </button>
          <div className="h-10 w-10 rounded-full bg-[#8a38f5] flex items-center justify-center text-white font-medium text-sm mr-3" data-id="element-9">
            MC
          </div>
          <div data-id="element-10">
            <h2 className="text-base font-bold text-[#1e1e1e]" data-id="element-11">Michael Chen</h2>
            <p className="text-xs text-gray-500" data-id="element-12">24 Tutoring Chats</p>
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="relative" data-id="element-13">
          <button ref={triggerRef} type="button" onClick={toggleDropdown} onKeyDown={handleTriggerKeyDown} aria-haspopup="listbox" aria-expanded={isDatePickerOpen} aria-label={`Select date range: ${displayLabel}`} className={`flex items-center px-4 py-2.5 border rounded-lg transition-all duration-150 cursor-pointer
              ${isDatePickerOpen ? 'border-[#531ac7] bg-white ring-2 ring-[#531ac7]/20' : 'border-gray-200 bg-white hover:bg-gray-50'}
              focus:outline-none focus:ring-2 focus:ring-[#531ac7]/30 focus:border-[#531ac7]`} data-id="element-14">
            <Calendar className="h-5 w-5 text-[#531ac7] mr-2.5" aria-hidden="true" data-id="element-15" />
            <span className="text-sm text-[#1e1e1e] font-medium" data-id="element-16">
              {displayLabel}
            </span>
            {isDatePickerOpen ? <ChevronUp className="h-4 w-4 text-[#1e1e1e] ml-3 transition-transform duration-200" aria-hidden="true" data-id="element-17" /> : <ChevronDown className="h-4 w-4 text-[#1e1e1e] ml-3 transition-transform duration-200" aria-hidden="true" data-id="element-18" />}
          </button>

          {/* Dropdown Panel */}
          {isDatePickerOpen && <div ref={dropdownRef} className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-2xl border-2 border-[#531ac7] shadow-lg shadow-black/10 z-50 overflow-hidden" style={{
          animation: 'dropdownIn 150ms ease-out'
        }} data-id="element-19">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4" data-id="element-20">
                <div className="flex items-center" data-id="element-21">
                  <Calendar className="h-6 w-6 text-[#531ac7] mr-3" aria-hidden="true" data-id="element-22" />
                  <span className="text-base font-medium text-[#1e1e1e]" data-id="element-23">
                    {displayLabel}
                  </span>
                </div>
                <button onClick={() => setIsDatePickerOpen(false)} className="p-1 rounded-md hover:bg-gray-100 transition-colors" aria-label="Close date picker" data-id="element-24">
                  <ChevronUp className="h-5 w-5 text-[#1e1e1e]" data-id="element-25" />
                </button>
              </div>

              {/* Segmented Control */}
              <div className="px-5 pb-4" data-id="element-26">
                <div className="bg-[#6c7683] rounded-xl p-1.5 flex relative" role="tablist" aria-label="Time period" data-id="element-27">
                  {TABS.map(tab => <button key={tab} role="tab" aria-selected={activeTab === tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative z-10 cursor-pointer
                        ${activeTab === tab ? 'bg-[#531ac7] text-white shadow-lg shadow-[#531ac7]/40 font-semibold' : 'text-white/90 hover:bg-white/10'}
                        focus:outline-none focus:ring-2 focus:ring-white/30`} data-id="element-28">
                      {tab}
                    </button>)}
                </div>
              </div>

              {/* Date Range List */}
              <div className="px-3 pb-3 max-h-[320px] overflow-y-auto" role="listbox" aria-label={`${activeTab}ly date ranges`} data-id="element-29">
                {currentOptions.map((option, index) => {
              const isSelected = selectedIndex[activeTab] === index;
              const isFocused = focusedIndex === index;
              const optionLabel = option.label ? `${option.label}  •  ${option.range}` : option.range;
              return <button key={`${activeTab}-${index}`} ref={el => {
                optionRefs.current[index] = el;
              }} role="option" aria-selected={isSelected} onClick={() => selectOption(index)} onKeyDown={e => handleOptionKeyDown(e, index)} className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-colors duration-100 cursor-pointer mb-1
                        focus:outline-none
                        ${isSelected ? 'bg-[#bfa5f3]/40 text-[#1e1e1e]' : isFocused ? 'bg-[#f3eeff] text-[#1e1e1e]' : 'text-[#4b5563] hover:bg-[#f3eeff]'}`} data-id="element-30">
                      <Calendar className={`h-5 w-5 mr-4 flex-shrink-0 ${isSelected ? 'text-[#531ac7]' : 'text-[#531ac7]/70'}`} aria-hidden="true" data-id="element-31" />
                      <span className={`text-[15px] ${isSelected ? 'font-medium' : 'font-normal'}`} data-id="element-32">
                        {optionLabel}
                      </span>
                    </button>;
            })}
              </div>
            </div>}
        </div>
      </header>

      {/* Chat Content */}
      <div className="flex-1 overflow-y-auto p-8" data-id="element-33">
        {/* Date Subheader */}
        <div className="flex items-center justify-between mb-8" data-id="element-34">
          <h3 className="text-gray-500 font-medium" data-id="element-35">Tuesday, January 2</h3>
          <div className="flex items-center space-x-6" data-id="element-36">
            <div className="flex items-center text-gray-500 text-sm" data-id="element-37">
              <BarChart2 className="h-4 w-4 text-[#8a38f5] mr-2" data-id="element-38" />
              <span data-id="element-39">9 Checkpoints</span>
            </div>
            <div className="flex items-center text-gray-500 text-sm" data-id="element-40">
              <MessageSquare className="h-4 w-4 text-[#8a38f5] mr-2" data-id="element-41" />
              <span data-id="element-42">27 Messages</span>
            </div>
            <div className="flex items-center text-[#1e1e1e] text-sm font-medium" data-id="element-43">
              <div className="h-2 w-2 rounded-full bg-[#8a38f5] mr-2" data-id="element-44"></div>
              <span data-id="element-45">Checkpoint Navigator</span>
            </div>
          </div>
        </div>

        {/* Checkpoint Marker */}
        <div className="flex items-center mb-8" data-id="element-46">
          <div className="bg-[#8a38f5] text-white px-3 py-1.5 rounded-full flex items-center text-sm font-medium mr-3 shadow-sm" data-id="element-47">
            <MapPin className="h-3 w-3 mr-1.5 fill-current" data-id="element-48" />
            Checkpoint 1
          </div>
          <span className="text-gray-400 text-sm" data-id="element-49">2:14 PM • Messages 1-10</span>
        </div>

        {/* Messages */}
        <div className="max-w-3xl" data-id="element-50">
          <ChatMessage sender="ai" name="AI tutor" avatarInitials="AI" timestamp="2:14 PM" message="Hi Michael! Ready to work on linear functions today? I saw you had some questions from the homework." data-id="element-51" />

          <ChatMessage sender="student" name="Michael" avatarInitials="M" timestamp="2:16 PM" message="Yeah, I'm really confused about slope-intercept form. I don't understand what m and b actually mean." data-id="element-52" />

          <ChatMessage sender="ai" name="AI tutor" avatarInitials="AI" timestamp="2:17 PM" message="Great question! Let's break it down. The slope-intercept form is y = mx + b, where m is the slope and b is the y-intercept." data-id="element-53" />

          <ChatMessage sender="student" name="Michael" avatarInitials="M" timestamp="2:18 PM" message="So the slope is like how much it goes up or down?" data-id="element-54" />

          <ChatMessage sender="ai" name="AI tutor" avatarInitials="AI" timestamp="2:18 PM" message="Exactly! The slope tells you how much y changes when x increases by 1. If the slope is 2, that means for every 1 unit you move right, you move up 2 units." data-id="element-55" />
        </div>
      </div>
    </main>;
}